import type { CSSProperties, FC, MouseEvent, ReactNode } from "react";
import { Eye, Pencil, Archive, Trash2, Download, type LucideIcon } from "lucide-react";
import StyledTooltip from "./StyledTooltip/StyledTooltip";

type IconName = "view" | "edit" | "archive" | "delete" | "unarchive" | "download";

interface CustomIconProps {
  name: IconName;
  tooltip?: string;
  onClick?: (event: MouseEvent<Element>) => void;
  disabled?: boolean;
  size?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
  placement?: "top" | "bottom" | "left" | "right";
}

const iconMap: Partial<Record<IconName, LucideIcon>> = {
  view: Eye,
  edit: Pencil,
  archive: Archive,
  delete: Trash2,
  download: Download,
};

export const CustomIcon: FC<CustomIconProps> = ({
  name,
  tooltip,
  onClick,
  disabled = false,
  size = 18,
  color = "#141B34",
  className = "audience-actionbuttons",
  style: styleProp = {},
  placement = "top",
}) => {
  const handleClick = (event: MouseEvent<Element>) => {
    event.stopPropagation();
    if (!disabled) {
      onClick?.(event);
    }
  };
  const style: CSSProperties = disabled
    ? { cursor: "not-allowed", opacity: 0.4, ...styleProp }
    : { cursor: "pointer", ...styleProp };

  let iconElement: ReactNode;

  if (name === "unarchive") {
    iconElement = (
      <img
        src="/Icons/unarchieve-icon.svg"
        alt="Unarchive"
        width={size}
        height={size}
        className={className}
        style={style}
        aria-disabled={disabled}
        onClick={handleClick}
      />
    );
  } else {
    const IconComponent = iconMap[name];
    if (!IconComponent) {
      return null;
    }
    iconElement = (
      <IconComponent
        size={size}
        style={{ color, ...style }}
        aria-disabled={disabled}
        className={className}
        onClick={handleClick}
      />
    );
  }

  return tooltip ? (
    <StyledTooltip content={tooltip} placement={placement}>
      <span>{iconElement}</span>
    </StyledTooltip>
  ) : (
    iconElement
  );
};
